import React, { useCallback, useState } from "react";

import { setCookie } from "cookies-next";
import { FormattedMessage } from "react-intl";

import { Text } from "@/components/atoms/Text";
import { RecommendedListingThumbnail } from "@/components/features/listings/ListingThumbnail";
import { type CarouselApi } from "@/components/molecules/Carousel";
import {
  CarouselSection,
  CarouselSectionArrow,
} from "@/components/organisms/CarouselSection";
import { SaveListingInfo } from "~/components/save/SaveListing.types";
import useRedirectToSigninSignup from "~/hooks/useRedirectToSigninSignup";
import { IAttachment, IRecommendedListings } from "~/types/api-types";

interface RecommendedListingsProps {
  listingId: string;
  recommendedListings: IRecommendedListings[];
  city: string;
  useType: string;
  allAttachments: IAttachment[];
  openSaveListingFlow: (listingInfo: SaveListingInfo) => void;
}

const RecommendedListings: React.FC<RecommendedListingsProps> = ({
  listingId,
  recommendedListings,
  city,
  useType,
  allAttachments,
  openSaveListingFlow,
}) => {
  const { handleRedirectToSignupPage, isUserLoggedIn } =
    useRedirectToSigninSignup();

  const handleSavedInteraction = useCallback(
    (listing: IRecommendedListings) => {
      if (!isUserLoggedIn) {
        setCookie("listing_pending_save", listing._id);
        handleRedirectToSignupPage("fav");
      } else {
        openSaveListingFlow({
          listingId: listing._id,
          listingImg: listing.images?.[0]?.external_id ?? "",
          listingTitle: listing.title,
          listingLocation: listing.neighborhood,
          listingCapacity: listing?.number_guests || 0,
        });
      }
    },
    [isUserLoggedIn, handleRedirectToSignupPage, openSaveListingFlow],
  );

  const [api, setApi] = useState<CarouselApi>();

  const carouselHeader = (
    <div className="tw-flex tw-justify-between tw-mb-48">
      <Text element="h3" typeface="headline-5-b">
        <FormattedMessage
          id="listing.listing_main.recommended_listing_header_popular"
          defaultMessage="Popular {useType} spaces near {city}"
          values={{
            city: city,
            useType: useType?.toLowerCase(),
          }}
        />
      </Text>
      {recommendedListings.length > 1 && (
        <div className="tw-hidden md:tw-flex tw-gap-24 tw-ml-24">
          <CarouselSectionArrow
            direction="left"
            api={api}
            onClick={() => {
              window.analyticsIntegrations.listingCarouselViewed(
                listingId,
                "recommended-listings",
                "control",
              );
            }}
          />
          <CarouselSectionArrow
            direction="right"
            api={api}
            onClick={() => {
              window.analyticsIntegrations.listingCarouselViewed(
                listingId,
                "recommended-listings",
                "control",
              );
            }}
          />
        </div>
      )}
    </div>
  );

  return (
    <div className="tw-py-80 tw-bg-gray-200">
      <CarouselSection header={carouselHeader} setApi={setApi} disableSwipe>
        {recommendedListings.map((listing, index) => {
          const isSaved = allAttachments.some(
            (attachment) => attachment.value === listing._id,
          );
          return (
            <div
              key={`recommended-listing-${listing._id}`}
              role="button"
              tabIndex={0}
              onClick={() => {
                window.analyticsIntegrations.listingCarouselImpression(
                  listingId,
                  listing._id,
                  "popular_listings",
                  index,
                );
              }}
              onKeyPress={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  window.analyticsIntegrations.listingCarouselImpression(
                    listingId,
                    listing._id,
                    "popular_listings",
                    index,
                  );
                }
              }}
            >
              <RecommendedListingThumbnail
                key={`recommended-listing-${listing._id}`}
                imageHeight={285}
                listing={listing}
                onThumbnailClick={() => {}}
                isSaved={isSaved}
                pricePerHourText="/hr"
                displayFromText="from"
                displayFrom
                interaction={() => handleSavedInteraction(listing)}
              />
            </div>
          );
        })}
      </CarouselSection>
      {recommendedListings.length > 1 && (
        <div className="tw-flex md:tw-hidden tw-justify-between tw-ml-24 tw-mr-16 tw-mt-24">
          <CarouselSectionArrow
            direction="left"
            api={api}
            onClick={() => {
              window.analyticsIntegrations.listingCarouselViewed(
                listingId,
                "recommended-listings",
                "control",
              );
            }}
          />
          <CarouselSectionArrow
            direction="right"
            api={api}
            onClick={() => {
              window.analyticsIntegrations.listingCarouselViewed(
                listingId,
                "recommended-listings",
                "control",
              );
            }}
          />
        </div>
      )}
    </div>
  );
};

export default RecommendedListings;
